import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  CourtOptionPaginationControls,
  SearchOption,
  SearchOptionPaginationControl,
} from "../types/searchSessions";
import type { SearchData, SearchMode } from "../types";
import logger from "../utils/logger";
import {
  getSearchBenches,
  getSearchBenchRooms,
  getSearchComplexes,
  getSearchCourtRooms,
  getSearchDistricts,
  getSearchEstablishments,
  getSearchHighCourts,
} from "../api/searchApis";
import { indianStateSelectOptions } from "../utils/indianStateOptions";

const DROPDOWN_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 250;

type SearchApiFn = (
  payload: any,
  pagination?: { page?: number; limit?: number },
) => Promise<any>;

interface UsePaginatedOptionsParams<T> {
  enabled: boolean;
  apiFn: SearchApiFn;
  payload: any;
  resetKey: string;
  mapper: (item: T) => SearchOption;
}

const emptyPaginationControl: SearchOptionPaginationControl = {
  onSearchChange: () => undefined,
  onLoadMore: () => undefined,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
};

const mergeOptions = (
  currentOptions: SearchOption[],
  nextOptions: SearchOption[],
) => {
  const seen = new Set(currentOptions.map((option) => String(option.value)));
  const merged = [...currentOptions];

  nextOptions.forEach((option) => {
    const key = String(option.value);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(option);
    }
  });

  return merged;
};

const usePaginatedOptions = <T,>({
  enabled,
  apiFn,
  payload,
  resetKey,
  mapper,
}: UsePaginatedOptionsParams<T>) => {
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const payloadRef = useRef(payload);
  const mapperRef = useRef(mapper);
  const optionCacheRef = useRef<Map<string, SearchOption>>(new Map());

  useEffect(() => {
    payloadRef.current = payload;
  }, [resetKey, payload]);

  useEffect(() => {
    mapperRef.current = mapper;
  }, [mapper]);

  useEffect(() => {
    optionCacheRef.current.clear();
  }, [resetKey]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setKeyword(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (!enabled) return;

      append ? setIsLoadingMore(true) : setIsLoading(true);

      try {
        const res = await apiFn(
          { ...payloadRef.current, keyword },
          { page: nextPage, limit: DROPDOWN_PAGE_SIZE },
        );

        if (!res?.status) {
          if (!append) {
            setOptions([]);
          }
          setPage(1);
          setTotalPages(1);
          return;
        }

        const nextOptions = (res.data || []).map(mapperRef.current);
        const pagination = res.pagination || {};
        const nextTotalPages = Number(pagination.totalPages) || 1;

        nextOptions.forEach((option: SearchOption) => {
          optionCacheRef.current.set(String(option.value), option);
        });

        setOptions((currentOptions) => {
          if (append) {
            return mergeOptions(currentOptions, nextOptions);
          }

          if (keyword) {
            return nextOptions;
          }

          return mergeOptions(
            nextOptions,
            Array.from(optionCacheRef.current.values()),
          );
        });
        setPage(nextPage);
        setTotalPages(nextTotalPages);
      } catch (error: any) {
        if (!append) {
          setOptions([]);
        }
        setPage(1);
        setTotalPages(1);
        logger.error(error);
      } finally {
        append ? setIsLoadingMore(false) : setIsLoading(false);
      }
    },
    [apiFn, enabled, keyword],
  );

  useEffect(() => {
    if (!enabled) {
      setOptions([]);
      setPage(1);
      setTotalPages(1);
      setSearchInput("");
      setKeyword("");
      return;
    }

    fetchPage(1, false);
  }, [enabled, fetchPage, resetKey]);

  const onLoadMore = useCallback(() => {
    if (!enabled || isLoading || isLoadingMore || page >= totalPages) return;
    fetchPage(page + 1, true);
  }, [enabled, fetchPage, isLoading, isLoadingMore, page, totalPages]);

  const control = useMemo<SearchOptionPaginationControl>(
    () => ({
      onSearchChange: setSearchInput,
      onLoadMore,
      hasMore: enabled && page < totalPages,
      isLoading,
      isLoadingMore,
    }),
    [enabled, isLoading, isLoadingMore, onLoadMore, page, totalPages],
  );

  return { options, control };
};

export const useCourtSessionOptions = (
  searchData: SearchData,
  searchMode: SearchMode,
) => {
  const states = useMemo(() => indianStateSelectOptions, []);

  const highCourtOptions = usePaginatedOptions({
    enabled: true,
    apiFn: getSearchHighCourts,
    payload: {},
    resetKey: "high-courts",
    mapper: (item: any) => ({
      label: item.highCourtName,
      value: item._id,
    }),
  });

  const benchOptions = usePaginatedOptions({
    enabled: Boolean(searchData.highcourt),
    apiFn: getSearchBenches,
    payload: { highcourt: searchData.highcourt },
    resetKey: `benches:${searchData.highcourt || ""}`,
    mapper: (item: any) => ({
      label: item.benchName,
      value: item._id,
    }),
  });

  const benchRoomOptions = usePaginatedOptions({
    enabled: Boolean(searchData.bench),
    apiFn: getSearchBenchRooms,
    payload: { bench: searchData.bench },
    resetKey: `bench-rooms:${searchData.bench || ""}`,
    mapper: (item: any) => ({
      label: `${item.courtRoom} - ${item.benchName}`,
      value: item._id,
    }),
  });

  const districtOptions = usePaginatedOptions({
    enabled: searchMode === "district",
    apiFn: getSearchDistricts,
    payload: { stateName: searchData.state },
    resetKey: `districts:${searchMode}:${searchData.state || ""}`,
    mapper: (item: any) => ({
      label: item.districtName,
      value: item._id,
      stateName: item.stateName,
    }),
  });

  const complexOptions = usePaginatedOptions({
    enabled: Boolean(searchData.district),
    apiFn: getSearchComplexes,
    payload: { district: searchData.district },
    resetKey: `complexes:${searchData.district || ""}`,
    mapper: (item: any) => ({
      label: item.complexName,
      value: item._id,
      courtEstablishment: item.courtEstablishment,
    }),
  });

  const courtEstablishment = useMemo(
    () =>
      complexOptions.options.find(
        (complex) => complex.value === searchData.courtComplex,
      )?.courtEstablishment,
    [complexOptions.options, searchData.courtComplex],
  );

  const establishmentOptions = usePaginatedOptions({
    enabled: Boolean(searchData.courtComplex),
    apiFn: getSearchEstablishments,
    payload: { complex: searchData.courtComplex },
    resetKey: `establishments:${searchData.courtComplex || ""}`,
    mapper: (item: any) => ({
      label: item.name,
      value: item._id,
    }),
  });

  const courtRoomParentId = searchData.establishment || searchData.courtComplex;
  const shouldLoadCourtRooms =
    Boolean(searchData.courtComplex) &&
    !(Boolean(courtEstablishment) && !searchData.establishment);
  const courtRoomOptions = usePaginatedOptions({
    enabled: shouldLoadCourtRooms,
    apiFn: getSearchCourtRooms,
    payload: { complex: courtRoomParentId },
    resetKey: `court-rooms:${courtRoomParentId || ""}:${courtEstablishment ? "with-establishment" : "direct"}`,
    mapper: (item: any) => ({
      label: `${item.roomNumber} - ${item.courtRoomName}`,
      value: item._id,
    }),
  });

  const controls: CourtOptionPaginationControls = {
    highCourts: highCourtOptions.control,
    benches: searchData.highcourt ? benchOptions.control : emptyPaginationControl,
    benchRooms: searchData.bench
      ? benchRoomOptions.control
      : emptyPaginationControl,
    districts:
      searchMode === "district" ? districtOptions.control : emptyPaginationControl,
    complexes: searchData.district
      ? complexOptions.control
      : emptyPaginationControl,
    establishments: searchData.courtComplex
      ? establishmentOptions.control
      : emptyPaginationControl,
    courtRooms: shouldLoadCourtRooms
      ? courtRoomOptions.control
      : emptyPaginationControl,
  };

  return {
    states,
    highCourts: highCourtOptions.options,
    benches: benchOptions.options,
    benchRooms: benchRoomOptions.options,
    districts: districtOptions.options,
    complexes: complexOptions.options,
    establishments: establishmentOptions.options,
    courtRooms: courtRoomOptions.options,
    courtEstablishment,
    controls,
  };
};
